import {alpha, createTheme, type Direction, type PaletteOptions, type ThemeOptions} from "@mui/material/styles"

import {typography, typographyNewLandingPage} from "./typography"
import {colors} from "./colors"

const spacing = 4

// Use this tool for named colors, e.g. vulcanSlate
// http://chir.ag/projects/name-that-color/
export const paletteColors = {
	// accents
	indebtedPrimary: colors.warmBlue["900"],
	slateNavy: "#2F384A",
	waterLeaf: "#A0E9E3",
	stratos: "#020452",
	stratosLight: "#4D4F85",
	stratosLight2: colors.heron["900"],

	// slate
	midnightSlate: "#12161F",
	darkSlate: "#636A77",
	highlightedSlate: "#1C232F",

	// reds
	warningRed: "#EF0909",
	warningButtonRed: "#D7153A",
	errorRed: "#E70808",
	blushRed: "#FEDADA",

	// greens
	discountGreen: "#368700",
	onlineGreen: "#2BA829",
	foamGreen: "#C4F4C3",

	// greyscale
	black: colors.primary.black,
	dustyGrey: "#979797",
	grey: "#979797",
	goneGrey: "#F3F3F4",
	white: colors.primary.white,

	// New Layout
	pageBackground: colors.purpleIllusion["100"],
}

const palette: PaletteOptions = {
	primary: {main: paletteColors.indebtedPrimary, contrastText: paletteColors.white},
	secondary: {main: paletteColors.slateNavy, contrastText: paletteColors.white},
}

const newLayoutPalette: PaletteOptions = {
	primary: {main: paletteColors.indebtedPrimary, contrastText: paletteColors.white},
	secondary: {main: colors.endlessGalaxy["900"], contrastText: paletteColors.white},
	error: {main: paletteColors.errorRed},
	warning: {main: paletteColors.warningRed},
	success: {main: paletteColors.discountGreen},
	info: {main: colors.tiffanyBlue["900"]},
	background: {default: colors.purpleIllusion["25"], paper: colors.primary.white},
	text: {primary: paletteColors.midnightSlate, secondary: colors.endlessGalaxy["400"]},
	divider: colors.gray["100"],
}

const components: ThemeOptions["components"] = {
	MuiCssBaseline: {
		styleOverrides: {
			body: {
				fontSize: "0.875rem",
				lineHeight: 1.43,
				letterSpacing: "0.01071em",
				backgroundColor: "#fafafa",
			},
		},
	},
	MuiInputLabel: {
		styleOverrides: {
			root: ({theme}) => ({
				transformOrigin: theme.direction === "rtl" ? "top right" : "top left",
				left: theme.direction === "rtl" ? "auto" : undefined,
				right: theme.direction === "rtl" ? "1rem" : "auto",
			}),
			outlined: ({theme}) => ({
				transform:
					theme.direction === "rtl" ? "translate(-6px, 20px) scale(1)" : "translate(14px, 20px) scale(1)",
				"&.MuiInputLabel-shrink": {
					transform:
						theme.direction === "rtl"
							? "translate(-3px, -9px) scale(0.75)"
							: "translate(14px, -9px) scale(0.75)",
				},
			}),
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: ({theme}) => ({
				"& .MuiOutlinedInput-notchedOutline": {
					textAlign: theme.direction === "rtl" ? "right" : "left",
				},
			}),
			input: ({theme}) => ({
				textAlign: theme.direction === "rtl" ? "right" : "left",
			}),
		},
	},
	MuiFormControl: {
		styleOverrides: {
			root: ({theme}) => ({
				"&.MuiInputBase-input": {
					textAlign: theme.direction === "rtl" ? "right" : "left",
				},
			}),
		},
	},
	MuiInputBase: {
		styleOverrides: {
			input: ({theme}) => ({
				textAlign: theme.direction === "rtl" ? "right" : "left",
			}),
		},
	},
	MuiTypography: {
		styleOverrides: {
			root: ({theme, ownerState}) => ({
				// Only apply RTL text alignment if no explicit align prop is set
				...(ownerState.align === undefined && {
					textAlign: theme.direction === "rtl" ? "right" : "left",
				}),
			}),
		},
	},
	MuiFormHelperText: {
		styleOverrides: {
			root: ({theme}) => ({
				textAlign: theme.direction === "rtl" ? "right" : "left",
				marginLeft: theme.direction === "rtl" ? "auto" : "14px",
				marginRight: theme.direction === "rtl" ? "14px" : "auto",
			}),
		},
	},
	MuiButton: {
		defaultProps: {
			color: "primary",
			variant: "contained",
		},
		styleOverrides: {
			root: {
				textTransform: "none",
			},
			startIcon: ({theme}) => ({
				marginLeft: theme.direction === "rtl" ? "8px" : "-4px",
				marginRight: theme.direction === "rtl" ? "-4px" : "8px",
			}),
		},
	},
	MuiIconButton: {
		defaultProps: {
			color: "primary",
		},
	},
	MuiListItem: {
		defaultProps: {
			dense: true,
		},
	},
	MuiAppBar: {
		defaultProps: {
			elevation: 1,
		},
	},
	MuiContainer: {
		styleOverrides: {
			root: ({theme}) => ({
				paddingTop: theme.spacing(3),
			}),
		},
	},
	MuiTableCell: {
		styleOverrides: {
			head: ({theme}) => ({
				color: theme.palette.text.secondary,
				fontSize: "0.875rem",
			}),
		},
	},
	MuiLink: {
		defaultProps: {
			underline: "hover",
		},
		styleOverrides: {
			root: ({theme}) => ({
				direction: theme.direction,
			}),
		},
	},
}

const newLandingPageComponents: ThemeOptions["components"] = {
	MuiCssBaseline: {
		styleOverrides: {
			body: ({theme}: {theme: any}) => ({
				fontSize: "0.875rem",
				lineHeight: "1.25rem",
				letterSpacing: "0.01071em",
				backgroundColor: theme.palette.background.default,
			}),
		},
	},
	MuiTextField: {
		defaultProps: {
			variant: "outlined",
			fullWidth: true,
		},
	},
	MuiInputLabel: {
		styleOverrides: {
			root: ({theme}) => ({
				transformOrigin: theme.direction === "rtl" ? "top right" : "top left",
				left: theme.direction === "rtl" ? "auto" : undefined,
				right: theme.direction === "rtl" ? "1rem" : "auto",
			}),
			outlined: ({theme}) => ({
				transform:
					theme.direction === "rtl"
						? "translate(-0.375rem, 1.25rem) scale(1)"
						: "translate(0.875rem, 1.25rem) scale(1)",
				"&.MuiInputLabel-shrink": {
					transform:
						theme.direction === "rtl"
							? "translate(-0.1875rem, -0.5625rem) scale(0.75)"
							: "translate(0.875rem, -0.5625rem) scale(0.75)",
				},
			}),
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: ({theme}) => ({
				"& .MuiOutlinedInput-notchedOutline": {
					textAlign: theme.direction === "rtl" ? "right" : "left",
					borderColor: theme.palette.grey[400],
					borderRadius: "8px",
				},
				"&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
					borderColor: theme.palette.text.primary,
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderColor: theme.palette.primary.main,
					borderWidth: 2,
				},
			}),
			input: ({theme}) => ({
				textAlign: theme.direction === "rtl" ? "right" : "left",
			}),
		},
	},
	MuiFormControl: {
		styleOverrides: {
			root: ({theme}) => ({
				"& .MuiInputBase-input": {
					textAlign: theme.direction === "rtl" ? "right" : "left",
				},
			}),
		},
	},
	MuiTypography: {
		styleOverrides: {
			root: ({theme, ownerState}) => ({
				// Only apply RTL text alignment if no explicit align prop is set
				...(ownerState.align === undefined && {
					textAlign: theme.direction === "rtl" ? "right" : "left",
				}),
			}),
		},
	},
	MuiFormHelperText: {
		styleOverrides: {
			root: ({theme}) => ({
				textAlign: theme.direction === "rtl" ? "right" : "left",
				marginLeft: theme.direction === "rtl" ? "auto" : theme.spacing(3.5),
				marginRight: theme.direction === "rtl" ? theme.spacing(3.5) : "auto",
			}),
		},
	},
	MuiButton: {
		defaultProps: {
			color: "primary",
			variant: "contained",
			disableElevation: true,
		},
		styleOverrides: {
			root: ({theme}) => ({
				textTransform: "none",
				borderRadius: "8px",
				fontWeight: 600,
				fontSize: "16px",
				lineHeight: "1.5rem",
				padding: "0.625rem 1rem",
				border: `1px solid ${theme.palette.primary.main}`,
				backgroundColor: theme.palette.primary.main,
				boxShadow: "0 1px 2px 0 rgba(25, 89, 135, 0.05)",
				"&:hover": {
					backgroundColor: colors.warmBlue["700"],
					borderColor: colors.warmBlue["700"],
				},
				"&:disabled, &.Mui-disabled": {
					border: `1px solid ${colors.warmBlue["200"]}`,
					backgroundColor: colors.warmBlue["200"],
				},
			}),
			startIcon: ({theme}) => ({
				marginLeft: theme.direction === "rtl" ? "8px" : "-4px",
				marginRight: theme.direction === "rtl" ? "-4px" : "8px",
			}),
		},
	},
	MuiButtonGroup: {
		defaultProps: {variant: "contained", color: "primary"},
		styleOverrides: {
			root: {borderRadius: "8px"},
			grouped: ({theme}) => ({
				borderColor: alpha(theme.palette.primary.main, 0.2),
				"&:not(:last-of-type)": {borderRightColor: alpha(theme.palette.primary.main, 0.2)},
				"&.Mui-disabled": {opacity: 0.5},
			}),
		},
	},
	MuiToggleButtonGroup: {
		styleOverrides: {
			root: {borderRadius: "8px"},
		},
	},
	MuiToggleButton: {
		defaultProps: {color: "primary"},
		styleOverrides: {
			root: ({theme}) => ({
				textTransform: "none",
				borderRadius: "8px",
				borderColor: alpha(theme.palette.primary.main, 0.2),
				"&.Mui-selected": {
					backgroundColor: alpha(theme.palette.primary.main, 0.12),
					borderColor: alpha(theme.palette.primary.main, 0.4),
				},
				"&:hover": {backgroundColor: alpha(theme.palette.primary.main, 0.08)},
			}),
		},
	},
	MuiIconButton: {
		defaultProps: {
			color: "primary",
		},
	},
	MuiPaper: {
		defaultProps: {
			elevation: 0,
		},
		styleOverrides: {
			rounded: {
				borderRadius: "8px",
			},
			outlined: ({theme}) => ({
				borderColor: theme.palette.divider,
			}),
		},
	},
	MuiTooltip: {
		defaultProps: {arrow: true},
		styleOverrides: {
			tooltip: ({theme}) => ({
				borderRadius: "8px",
				fontSize: "0.75rem",
				backgroundColor: theme.palette.grey[900],
			}),
			arrow: ({theme}) => ({
				color: theme.palette.grey[900],
			}),
		},
	},
	MuiListItem: {
		defaultProps: {
			dense: true,
		},
	},
	MuiAppBar: {
		defaultProps: {
			elevation: 1,
		},
	},
	MuiContainer: {
		styleOverrides: {
			root: ({theme}) => ({paddingTop: theme.spacing(3)}),
		},
	},
	MuiTableCell: {
		styleOverrides: {
			head: {
				color: paletteColors.grey,
				fontSize: "0.875rem",
			},
			body: ({theme}) => ({
				borderBottomColor: theme.palette.divider,
				paddingTop: theme.spacing(3),
				paddingBottom: theme.spacing(3),
			}),
		},
	},
	MuiLink: {
		defaultProps: {
			underline: "hover",
		},
		styleOverrides: {
			root: ({theme}) => ({
				direction: theme.direction,
			}),
		},
	},
	MuiTabs: {
		styleOverrides: {
			indicator: ({theme}) => ({
				height: 2,
				backgroundColor: theme.palette.primary.main,
			}),
		},
	},
	MuiTab: {
		styleOverrides: {
			root: {
				textTransform: "none",
				minHeight: 48,
				fontWeight: 600,
			},
		},
	},
	MuiDialog: {
		styleOverrides: {
			paper: ({theme}) => ({
				borderRadius: "8px",
				backgroundColor: theme.palette.background.paper,
			}),
		},
	},
	MuiDialogTitle: {
		styleOverrides: {
			root: ({theme}) => ({
				padding: theme.spacing(3, 3, 2),
				borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
				fontWeight: 700,
			}),
		},
	},
	MuiDialogActions: {
		styleOverrides: {
			root: ({theme}) => ({
				padding: theme.spacing(2, 3, 3),
				gap: theme.spacing(1.5),
			}),
		},
	},
	MuiBackdrop: {
		styleOverrides: {
			root: ({theme}) => ({
				backgroundColor: alpha(theme.palette.secondary.main, 0.4),
			}),
		},
	},
	MuiSnackbar: {
		defaultProps: {
			anchorOrigin: {vertical: "bottom", horizontal: "center"},
			autoHideDuration: 6000,
		},
	},
	MuiPopover: {
		styleOverrides: {
			paper: {
				borderRadius: "8px",
			},
		},
	},
	MuiSnackbarContent: {
		styleOverrides: {
			root: {
				borderRadius: "8px",
			},
		},
	},
	MuiChip: {
		styleOverrides: {
			root: {
				borderRadius: "8px",
			},
		},
	},
	MuiSelect: {
		defaultProps: {
			variant: "outlined",
			fullWidth: true,
		},
		styleOverrides: {
			icon: ({theme}) => ({
				color: theme.palette.text.secondary,
			}),
		},
	},
	MuiMenuItem: {
		styleOverrides: {
			root: ({theme}) => ({
				minHeight: 40,
				borderRadius: "8px",
				"&.Mui-selected": {
					backgroundColor: alpha(theme.palette.primary.main, 0.08),
				},
			}),
		},
	},
	MuiCheckbox: {
		defaultProps: {color: "primary"},
		styleOverrides: {
			root: ({theme}) => ({
				borderRadius: "6px",
				"&.Mui-checked": {color: theme.palette.primary.main},
			}),
		},
	},
	MuiRadio: {
		defaultProps: {color: "primary"},
	},
	MuiSwitch: {
		defaultProps: {color: "primary"},
		styleOverrides: {
			track: ({theme}) => ({
				backgroundColor: theme.palette.grey[400],
				opacity: 1,
			}),
			thumb: {boxShadow: "none"},
			colorPrimary: ({theme}) => ({
				"&.Mui-checked + .MuiSwitch-track": {
					backgroundColor: theme.palette.primary.main,
					opacity: 1,
				},
			}),
		},
	},
	MuiFormControlLabel: {
		styleOverrides: {
			label: ({theme}) => ({
				color: theme.palette.text.primary,
			}),
		},
	},
	MuiDivider: {
		styleOverrides: {
			root: {
				borderColor: colors.gray["100"],
				margin: "1rem 0",
			},
		},
	},
	MuiCard: {
		defaultProps: {
			elevation: 1,
		},
		styleOverrides: {
			root: {
				borderRadius: "8px",
				boxShadow: "0 4px 14px 2px rgba(54, 59, 100, 0.10)",
			},
		},
	},
	MuiCardHeader: {
		styleOverrides: {
			root: ({theme}) => ({padding: theme.spacing(2, 2, 1.5)}),
			subheader: ({theme}) => ({color: theme.palette.text.secondary}),
		},
	},
	MuiCardActions: {
		styleOverrides: {
			root: ({theme}) => ({padding: theme.spacing(1.5, 2, 2), gap: theme.spacing(1)}),
		},
	},
	MuiTable: {
		styleOverrides: {
			root: {
				borderCollapse: "separate",
				borderSpacing: 0,
			},
		},
	},
}

const theme = createTheme({
	spacing,
	palette,
	typography,
	components,
})

const themeNewLandingPage = createTheme({
	spacing,
	palette: newLayoutPalette,
	shape: {borderRadius: "8px"},
	typography: typographyNewLandingPage,
	components: newLandingPageComponents,
})

function createLocaleTheme(direction: Direction, baseTheme = theme) {
	return createTheme(baseTheme, {direction})
}

export {theme, themeNewLandingPage, createLocaleTheme}
export {AppThemeProvider} from "./AppTheme"
